import {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
  useEffectEvent,
} from 'react';
import { useParams } from 'react-router';
import { Alert, Button, Flex, Input, Tabs, Timeline, Tooltip } from 'antd';
import Icon, {
  ExceptionOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import DecagonLogo from 'assets/decagon.svg?react';
import ZendeskLogo from 'assets/zendesk.svg?react';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

import useCustomerInfo from 'hooks/useCustomerInfo';
import useAccountQuery from 'hooks/useAccountQuery';
import useApplicationQuery from 'hooks/useApplicationQuery';
import useTimeoutValue from 'hooks/useTimeoutValue';

import { INTERACTIONS } from 'queries/interactions';

import { AGENT_ACTIVITY_TYPE, SIZE, STATUS } from 'utilities/constants';
import {
  formatDate,
  formatDateToSubmit,
  getAgentLocalTimestamp,
} from 'utilities/datesAndTimes';
import { isAgentNote, isZendeskInteraction } from 'utilities/helpers';

import AgentNote from './AgentNote';
import ZendeskInteraction from './ZendeskInteraction';

const TAB_ITEMS = [
  {
    key: 'all',
    label: 'All',
  },
  {
    key: 'notes',
    label: 'Notes',
  },
  {
    key: 'zendesk',
    label: 'Zendesk',
  },
];

const TimelineIcon = ({ interaction }) => {
  let backgroundColor;
  let color;
  let iconComponent;

  let type = interaction?.activityType;
  if (
    type === AGENT_ACTIVITY_TYPE.AGENT_NOTE ||
    type === AGENT_ACTIVITY_TYPE.AGENT_HISTORICAL_NOTE
  ) {
    if (interaction.isSticky) {
      type = 'sticky';
    }
    if (interaction.agentId === 'decagon') {
      type = 'ai';
    }
  }

  switch (type) {
    case AGENT_ACTIVITY_TYPE.AGENT_NOTE:
    case AGENT_ACTIVITY_TYPE.AGENT_HISTORICAL_NOTE:
      backgroundColor = 'var(--purple-9)';
      color = 'var(--purple-2)';
      iconComponent = UserOutlined;
      break;

    case AGENT_ACTIVITY_TYPE.AGENT_INTERACTION:
      backgroundColor = 'var(--orange-9)';
      color = 'var(--orange-2)';
      iconComponent = TagOutlined;
      break;

    case AGENT_ACTIVITY_TYPE.ZENDESK_EVENT:
      backgroundColor = 'var(--green-9)';
      color = 'var(--green-2)';
      iconComponent = ZendeskLogo;
      break;

    case 'sticky':
      backgroundColor = 'var(--yellow-9)';
      color = 'var(--yellow-2)';
      iconComponent = ExceptionOutlined;
      break;

    case 'ai':
      backgroundColor = 'var(--blue-9)';
      color = 'var(--blue-2)';
      iconComponent = DecagonLogo;
      break;

    default:
      backgroundColor = 'var(--color-border-secondary)';
      color = 'var(--color-text)';
      iconComponent = ClockCircleOutlined;
  }

  return (
    <div
      style={{
        borderRadius: '50%',
        height: 'var(--spacing-lg)',
        width: 'var(--spacing-lg)',
        backgroundColor: backgroundColor,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Icon
        component={iconComponent}
        style={{ fontSize: 'var(--font-size)', color }}
      />
    </div>
  );
};

// This should work for now but this probably needs yet another
// refactor to split functionality for customer/application.
// Theres potential for things to just get messier having to switch between
// customer and application logic
const NotesAndZendesk = () => {
  const { customerId, applicationId } = useParams();
  const { customerInfo, customerInfoQuery } = useCustomerInfo();

  const { simpleAccounts } = useAccountQuery(customerId);
  const { application } = useApplicationQuery(applicationId);

  const [pagination, setPagination] = useState(1);
  const rootRef = useRef(null);
  const [tabKey, setTabkey] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showLoadMoreFailure, setShowLoadMoreFailure] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [interactions, setInteractions] = useState([]);

  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState('');

  const [successMessage, setSuccessMessage] = useState('');
  const [resultsMessage, setResultsMessage] = useState('');
  const [commentAddOrUpdateSuccess, setCommentAddOrUpdateSuccess] =
    useTimeoutValue(false, 3000);

  const originationDate = useMemo(() => {
    if (applicationId && application) {
      return dayjs(application.submittedAt).toISOString();
    }

    if (customerId && simpleAccounts && simpleAccounts.length > 0) {
      const accountCreationDates = simpleAccounts.map(
        (account) => account.createdAt && dayjs(account.createdAt),
      );
      return dayjs.min(accountCreationDates).toISOString();
    }
  }, [customerId, simpleAccounts, applicationId, application]);

  const getDateRange = (index = 1) => {
    return {
      startDate: dayjs().subtract(90 * index, 'days'),
      endDate: dayjs().add(1, 'days'), // tomorrow  -i.e. include all events disregard of server timezone offset.
    };
  };

  const [getInteractions, interactionsQuery] = useLazyQuery(INTERACTIONS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const filteredInteractions = useMemo(() => {
    if (interactions) {
      const searching = searchFilter && searchFilter.trim().length > 0;

      let activities = [];

      interactions.forEach((interaction) => {
        let include = false;

        if (!interaction?.message) {
          return;
        }

        if (tabKey === 'all') {
          include =
            isZendeskInteraction(interaction) || isAgentNote(interaction);
        } else if (tabKey === 'notes') {
          include = isAgentNote(interaction);
        } else if (tabKey === 'zendesk') {
          include = isZendeskInteraction(interaction);
        }

        if (include && searching) {
          include = interaction.message
            .toLowerCase()
            .includes(searchFilter.toLowerCase());
        }

        if (include) {
          activities.push({
            icon: <TimelineIcon interaction={interaction} />,
            content: (
              <div
                key={`interaction-itm-${interaction.id}`}
                data-testid="interaction-item"
              >
                {isZendeskInteraction(interaction) ? (
                  <ZendeskInteraction
                    interaction={interaction}
                    highlight={searchFilter}
                  />
                ) : (
                  <AgentNote
                    interaction={interaction}
                    onCommentAddOrUpdateSuccess={(message) => {
                      setSuccessMessage(message);
                      setCommentAddOrUpdateSuccess(true);
                    }}
                    rootRef={rootRef}
                    highlight={searchFilter}
                  />
                )}
              </div>
            ),
          });
        }
      });
      return activities;
    }
  }, [interactions, tabKey, debouncedSearchFilter]);

  const loadInteractions = async (reset) => {
    let page = pagination + 1;
    if (reset) {
      page = 1;
      setPagination(1);
      setInitialLoad(true);
      setCanLoadMore(true);
    } else {
      // reset filter on Load More.
      // This is because active filter will hide the new records which inturn gives false impression.
      setSearchFilter('');
      setDebouncedSearchFilter('');
    }

    const { startDate, endDate } = getDateRange(page);

    setShowLoadMoreFailure(false);
    setIsLoading(true);

    try {
      const result = await getInteractions({
        variables: {
          customerId,
          applicationId,
          email: customerInfo?.email?.toLowerCase(),
          startDate: formatDateToSubmit(startDate),
          endDate: formatDateToSubmit(endDate),
        },
      });

      if (result.error) {
        throw result.error;
      }

      const beforeAccountCreation =
        originationDate && startDate.isBefore(originationDate);

      let messageUpdate = `Displaying notes from ${formatDate(
        startDate,
      )} to today. Click 'Load More' to view older entries.`;

      if (beforeAccountCreation) {
        messageUpdate = 'Displaying notes back to account creation.';
      }

      setInitialLoad(false);
      setPagination(page);
      setResultsMessage(messageUpdate);
      setCanLoadMore(!beforeAccountCreation);
    } catch (error) {
      if (!reset) {
        setShowLoadMoreFailure(true);
      }
    }

    setIsLoading(false);
  };

  const onTabChange = useCallback((key) => {
    setSearchFilter('');
    setDebouncedSearchFilter('');
    setTabkey(key);
  }, []);

  const debouncedSetSearchFilter = debounce(setDebouncedSearchFilter, 500);

  const handleSearchFilterChange = useCallback(
    (value) => {
      debouncedSetSearchFilter(value);
    },
    [interactions],
  );

  //reset notes state to make sure previous customer notes dont display on new customer
  useEffect(() => {
    setInteractions([]);
    setIsLoading(true);
  }, [customerId, applicationId]);

  useEffect(() => {
    if (!customerInfoQuery.loading && customerInfo.initialized) {
      loadInteractions(true);
    }
  }, [customerInfoQuery.loading, customerInfo]);

  // This is finally too annoying. With the loss of the onComplete function for queries,
  // we're stuck with a very awkard pattern. Needing a lazyQuery to make new requests
  // on demand, as well as needing a useEffect and useEffectEvent to be able to process those results based
  // cases where we need to refresh through notifyOnNetworkStatusChange. I want to work
  // a hook that will allow us to have pattern similar to the previous onComplete function
  // that abstracts all of this mess
  const processNewInteractions = useEffectEvent((newInteractions) => {
    setInteractions(
      newInteractions.map((interaction) => {
        if (
          interaction?.activityType === AGENT_ACTIVITY_TYPE.AGENT_INTERACTION
        ) {
          const messageParts = interaction?.message.split(' - ');
          const lastMessagePart = messageParts[messageParts.length - 1];

          // ex: Right Party Contact - CREDIT - PreIssuance - Provide Proof of Income - 2025-05-09 12:59PM
          const lastItemIsTimestamp = dayjs(lastMessagePart).isValid();

          if (lastItemIsTimestamp) {
            const localizedTimestamp = getAgentLocalTimestamp(lastMessagePart);
            messageParts[messageParts.length - 1] = localizedTimestamp;
          }

          return {
            ...interaction,
            message: messageParts.join(' - '),
          };
        }

        return interaction;
      }),
    );
  });

  useEffect(() => {
    if (!interactionsQuery.called || interactionsQuery.loading) {
      return;
    }

    const interactionsData =
      interactionsQuery.data?.notesInteractionsZendeskRecords || [];
    processNewInteractions(interactionsData);
  }, [
    interactionsQuery.called,
    interactionsQuery.loading,
    interactionsQuery.data,
  ]);

  return (
    <div style={{ overflow: 'auto' }}>
      <Tabs
        size="small"
        items={TAB_ITEMS}
        onTabClick={onTabChange}
        tabBarExtraContent={
          <Flex>
            <Input
              data-testid="notes-search"
              placeholder="Search.."
              allowClear
              type="text"
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                handleSearchFilterChange(e.target.value);
              }}
            />

            <Tooltip
              title={
                searchFilter?.trim().length > 0
                  ? 'Clear filter to refresh'
                  : 'Refresh'
              }
            >
              <Button
                type="link"
                icon={<ReloadOutlined />}
                disabled={searchFilter?.trim().length > 0}
                onClick={() => {
                  loadInteractions(true);
                }}
              />
            </Tooltip>
          </Flex>
        }
        style={{
          position: 'sticky',
          top: '0px',
          backgroundColor: 'var(--color-background)',
          zIndex: 2,
        }}
      ></Tabs>

      <QueryBoundary
        disabled={!initialLoad}
        messageHeight={'400px'}
        errorMessage="Failed to load Interactions. Please try the refresh button."
        query={interactionsQuery}
        skeleton={{
          title: false,
          paragraph: { rows: 12 },
        }}
        loadingOverride={customerInfoQuery.loading || isLoading}
      >
        <Timeline
          data-testid="notes-timeline"
          mode="start"
          items={filteredInteractions}
          style={{ paddingLeft: 0, paddingTop: '2px' }}
          styles={{
            itemIcon: {
              width: 'var(--spacing-lg)',
              zIndex: 1,
            },
            itemRail: {
              insetInlineStart: 'calc(var(--spacing-lg) / 2)',
            },
          }}
        />

        {searchFilter && !filteredInteractions.length ? (
          <SectionMessage
            status={STATUS.WARNING}
            size={SIZE.XS}
            text={`No results for: '${debouncedSearchFilter}'`}
          ></SectionMessage>
        ) : null}

        {resultsMessage && (
          <Alert
            data-testid="warning-message"
            type="warning"
            title={resultsMessage}
            style={{ marginBottom: 'var(--spacing-xs)' }}
          ></Alert>
        )}

        {showLoadMoreFailure && (
          <Alert
            data-testid="failure-message"
            type="error"
            title="Failed to load more interactions. Please try again."
            style={{ marginBottom: 'var(--spacing-xs)' }}
          ></Alert>
        )}

        <Button
          data-testid="load-more-interactions"
          type="primary"
          loading={isLoading}
          onClick={() => loadInteractions()}
          disabled={isLoading || !canLoadMore}
        >
          {canLoadMore ? 'Load More' : 'No more interactions'}
        </Button>
      </QueryBoundary>
      <SectionMessage
        status={STATUS.SUCCESS}
        size={SIZE.SM}
        text={successMessage}
        cover={true}
        visible={commentAddOrUpdateSuccess}
      ></SectionMessage>
    </div>
  );
};

export default NotesAndZendesk;
